/**
 * Canonical B2B cart add/merge for same-SKU lines.
 *
 * Shopify creates separate lines when line-item properties differ. Weight /
 * SerialNumbers / gold capture timestamps from Grid (QV), Order Sheet, and PDP
 * therefore used to produce duplicate rows for one SKU. Every storefront ATC
 * path should call addOrMergeItems() so same logical SKU stays one cart line.
 */
(function (w) {
  'use strict';

  if (w.AoneCartSkuMerge) return;

  var CART_JS = '/cart.js';
  var CART_ADD_JS = '/cart/add.js';
  var CART_CHANGE_JS = '/cart/change.js';
  var CART_UPDATE_JS = '/cart/update.js';

  var GOLD_PROP_KEYS = [
    '_gold_price_per_troy_oz',
    '_gold_price_captured_at',
    '_gold_price_material',
    '_gold_price_currency',
    '_gold_price_unit',
  ];

  /** @type {Record<string, 1>} */
  var IDENTITY_SKIP_KEYS = {
    Weight: 1,
    SelectedWeights: 1,
    selected_weights: 1,
    Weights: 1,
    weight: 1,
    SerialNumbers: 1,
    serialNumbers: 1,
    SerialNumber: 1,
    _cart_added_at: 1,
    _added_at: 1,
    added_at: 1,
    _source: 1,
    _view: 1,
    _from: 1,
    _page: 1,
    source: 1,
    view: 1,
  };
  GOLD_PROP_KEYS.forEach(function (key) {
    IDENTITY_SKIP_KEYS[key] = 1;
  });

  /** @type {Promise<unknown>} */
  var mutationChain = Promise.resolve();

  /**
   * Serialize cart mutations so concurrent Grid + PDP adds cannot both create lines.
   * @template T
   * @param {() => Promise<T>} task
   * @returns {Promise<T>}
   */
  function enqueue(task) {
    var run = mutationChain.then(task, task);
    mutationChain = run.then(
      function () {},
      function () {}
    );
    return run;
  }

  /** @param {unknown} value */
  function normalizeSku(value) {
    return String(value || '')
      .trim()
      .toUpperCase();
  }

  /** @param {string} raw */
  function splitCsv(raw) {
    return String(raw || '')
      .split(',')
      .map(function (part) {
        return String(part || '').trim();
      })
      .filter(Boolean);
  }

  /** @param {Record<string, unknown>|null|undefined} item */
  function getLineSku(item) {
    if (!item) return '';
    var direct = item.sku != null ? String(item.sku).trim() : '';
    if (direct) return direct;
    var props =
      item.properties && typeof item.properties === 'object'
        ? /** @type {Record<string, unknown>} */ (item.properties)
        : {};
    return props.SKU != null ? String(props.SKU).trim() : '';
  }

  /** @param {Record<string, unknown>|null|undefined} item */
  function getLineVariantId(item) {
    if (!item) return 0;
    var id = Number(item.variant_id != null ? item.variant_id : item.id);
    return Number.isFinite(id) && id > 0 ? id : 0;
  }

  /** @param {Record<string, unknown>|null|undefined} props */
  function readWeightCsv(props) {
    if (!props || typeof props !== 'object') return '';
    var keys = ['Weight', 'SelectedWeights', 'selected_weights', 'Weights', 'weight'];
    for (var i = 0; i < keys.length; i += 1) {
      var value = props[keys[i]];
      if (value != null && String(value).trim()) return String(value).trim();
    }
    return '';
  }

  /** @param {Record<string, unknown>|null|undefined} props */
  function readSerialCsv(props) {
    if (!props || typeof props !== 'object') return '';
    var keys = ['SerialNumbers', 'serialNumbers', 'SerialNumber'];
    for (var i = 0; i < keys.length; i += 1) {
      var value = props[keys[i]];
      if (value != null && String(value).trim()) return String(value).trim();
    }
    return '';
  }

  /**
   * Expand Weight + SerialNumbers into ordered unit pairs (serial is authoritative).
   * @param {string} weightCsv
   * @param {string} serialCsv
   * @returns {Array<{ weight: string, serial: string }>}
   */
  function expandSelectionUnits(weightCsv, serialCsv) {
    var weights = splitCsv(weightCsv);
    var serials = splitCsv(serialCsv);
    var count = Math.max(weights.length, serials.length, 0);
    /** @type {Array<{ weight: string, serial: string }>} */
    var units = [];
    for (var i = 0; i < count; i += 1) {
      units.push({
        weight: weights[i] || weights[0] || '',
        serial: serials[i] || '',
      });
    }
    return units;
  }

  /**
   * Merge selection units without dropping distinct serials that share a weight.
   * Duplicate serials are skipped (same physical piece).
   * @param {Array<{ weight: string, serial: string }>} existing
   * @param {Array<{ weight: string, serial: string }>} incoming
   */
  function mergeSelectionUnits(existing, incoming) {
    /** @type {Array<{ weight: string, serial: string }>} */
    var out = [];
    /** @type {Record<string, 1>} */
    var seenSerials = Object.create(null);

    function pushUnit(unit) {
      if (!unit) return;
      var serial = String(unit.serial || '').trim();
      var weight = String(unit.weight || '').trim();
      if (serial) {
        var serialKey = serial.toUpperCase();
        if (seenSerials[serialKey]) return;
        seenSerials[serialKey] = 1;
      } else if (!weight) {
        return;
      }
      out.push({ weight: weight, serial: serial });
    }

    (existing || []).forEach(pushUnit);
    (incoming || []).forEach(pushUnit);
    return out;
  }

  /**
   * @param {Array<{ weight: string, serial: string }>} units
   * @returns {{ weights: string, serials: string, quantity: number }}
   */
  function unitsToProperties(units) {
    var list = Array.isArray(units) ? units : [];
    var weights = list
      .map(function (unit) {
        return String(unit.weight || '').trim();
      })
      .filter(Boolean);
    var serials = list
      .map(function (unit) {
        return String(unit.serial || '').trim();
      })
      .filter(Boolean);
    return {
      weights: weights.join(', '),
      serials: serials.join(', '),
      quantity: Math.max(weights.length, serials.length, list.length),
    };
  }

  /**
   * Keep non-identity business props from the canonical line; overlay SKU/weight/serial.
   * Gold capture / added-at stay on the existing line so timestamps do not split identity.
   * @param {Record<string, unknown>} baseProps
   * @param {{ sku?: string, weightCsv?: string, serialCsv?: string, incomingProps?: Record<string, unknown> }} next
   */
  function buildCanonicalProperties(baseProps, next) {
    /** @type {Record<string, string>} */
    var props = {};
    var source = baseProps && typeof baseProps === 'object' ? baseProps : {};
    Object.keys(source).forEach(function (key) {
      if (IDENTITY_SKIP_KEYS[key]) return;
      if (source[key] == null) return;
      var value = String(source[key]).trim();
      if (value) props[key] = value;
    });

    GOLD_PROP_KEYS.forEach(function (key) {
      if (source[key] != null && String(source[key]).trim()) {
        props[key] = String(source[key]).trim();
      }
    });

    if (source._cart_added_at != null && String(source._cart_added_at).trim()) {
      props._cart_added_at = String(source._cart_added_at).trim();
    } else if (source._added_at != null && String(source._added_at).trim()) {
      props._cart_added_at = String(source._added_at).trim();
    }

    var incoming = next.incomingProps && typeof next.incomingProps === 'object' ? next.incomingProps : {};
    GOLD_PROP_KEYS.forEach(function (key) {
      if (!props[key] && incoming[key] != null && String(incoming[key]).trim()) {
        props[key] = String(incoming[key]).trim();
      }
    });

    var sku = String(next.sku || incoming.SKU || props.SKU || '').trim();
    if (sku) props.SKU = sku;

    var weightCsv = String(next.weightCsv || '').trim();
    var serialCsv = String(next.serialCsv || '').trim();
    if (weightCsv) props.Weight = weightCsv;
    else delete props.Weight;
    if (serialCsv) props.SerialNumbers = serialCsv;
    else delete props.SerialNumbers;

    return props;
  }

  /**
   * @param {Record<string, unknown>} cart
   * @param {string} sku
   * @param {number} [preferredVariantId]
   * @returns {Array<Record<string, unknown>>}
   */
  function findLinesForSku(cart, sku, preferredVariantId) {
    var target = normalizeSku(sku);
    if (!target || !cart || !Array.isArray(cart.items)) return [];
    /** @type {Array<Record<string, unknown>>} */
    var matches = [];
    cart.items.forEach(function (raw) {
      var item = /** @type {Record<string, unknown>} */ (raw);
      if (!item) return;
      if (normalizeSku(getLineSku(item)) !== target) return;
      matches.push(item);
    });
    if (!preferredVariantId || !matches.length) return matches;
    var preferred = matches.filter(function (item) {
      return getLineVariantId(item) === preferredVariantId;
    });
    return preferred.length ? preferred : matches;
  }

  /**
   * @param {Array<Record<string, unknown>>} lines
   * @param {number} [preferredVariantId]
   */
  function chooseCanonicalLine(lines, preferredVariantId) {
    if (!lines || !lines.length) return null;
    var pool = lines;
    if (preferredVariantId) {
      var preferred = lines.filter(function (item) {
        return getLineVariantId(item) === preferredVariantId;
      });
      if (preferred.length) pool = preferred;
    }
    return pool.slice().sort(function (a, b) {
      var ai = Number(a.index != null ? a.index : 0);
      var bi = Number(b.index != null ? b.index : 0);
      if (ai !== bi) return ai - bi;
      return String(a.key || '').localeCompare(String(b.key || ''));
    })[0];
  }

  /**
   * @param {string} url
   * @param {RequestInit} [opts]
   */
  async function fetchJson(url, opts) {
    var res = await fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {}));
    var data = {};
    try {
      data = await res.json();
    } catch (_) {
      data = {};
    }
    return { res: res, data: data };
  }

  async function fetchCart() {
    var wms = w.AOneWmsAvailability || w.WMSInventory;
    if (wms && typeof wms.fetchCart === 'function') {
      try {
        var fresh = await wms.fetchCart(true);
        if (fresh && Array.isArray(fresh.items)) return /** @type {Record<string, unknown>} */ (fresh);
      } catch (_) {}
    }
    var result = await fetchJson(CART_JS, { headers: { Accept: 'application/json' } });
    if (!result.res.ok) throw new Error('Unable to load cart');
    return /** @type {Record<string, unknown>} */ (result.data);
  }

  /**
   * Keep the in-memory WMS cart cache aligned when we reuse /cart/change.js payloads
   * instead of immediately re-fetching /cart.js. notify:false avoids per-SKU UI storms.
   * @param {Record<string, unknown>|null|undefined} cart
   * @returns {Record<string, unknown>|null|undefined}
   */
  function rememberCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return cart;
    var wms = w.AOneWmsAvailability || w.WMSInventory;
    if (wms && typeof wms.applyCart === 'function') {
      try {
        wms.applyCart(cart, { reason: 'sku-merge', notify: false });
      } catch (_) {}
    }
    return cart;
  }

  /**
   * @param {string} lineKey
   * @param {number} quantity
   * @param {Record<string, string>} [properties]
   */
  async function changeLineByKey(lineKey, quantity, properties) {
    /** @type {Record<string, unknown>} */
    var body = {
      id: String(lineKey),
      quantity: Math.max(0, Math.floor(quantity) || 0),
    };
    if (properties && typeof properties === 'object') body.properties = properties;
    var result = await fetchJson(CART_CHANGE_JS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!result.res.ok || result.data.errors || result.data.status) {
      var msg =
        (typeof result.data.description === 'string' && result.data.description) ||
        (typeof result.data.message === 'string' && result.data.message) ||
        (typeof result.data.errors === 'string' && result.data.errors) ||
        'Unable to update cart line';
      throw new Error(msg);
    }
    return /** @type {Record<string, unknown>} */ (result.data);
  }

  /**
   * @param {Record<string, number>} updates
   */
  async function updateLinesByKeys(updates) {
    var keys = Object.keys(updates || {});
    if (!keys.length) return null;
    var result = await fetchJson(CART_UPDATE_JS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });
    if (!result.res.ok || result.data.errors || result.data.status) {
      var msg =
        (typeof result.data.description === 'string' && result.data.description) ||
        (typeof result.data.message === 'string' && result.data.message) ||
        'Unable to update cart';
      throw new Error(msg);
    }
    return /** @type {Record<string, unknown>} */ (result.data);
  }

  /**
   * @param {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} items
   */
  async function addItemsRaw(items) {
    var result = await fetchJson(CART_ADD_JS, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items }),
    });
    if (!result.res.ok || result.data.status || result.data.message || result.data.errors) {
      var msg =
        (typeof result.data.description === 'string' && result.data.description) ||
        (typeof result.data.message === 'string' && result.data.message) ||
        'Unable to add items to cart';
      throw new Error(msg);
    }
    return result.data;
  }

  /**
   * Merge every same-SKU group into one Shopify line (caller must hold the queue lock).
   * @param {Record<string, unknown>} cart
   * @returns {Promise<{ cart: Record<string, unknown>, changed: boolean }>}
   */
  async function consolidateDuplicatesUnlocked(cart) {
    var current = cart && Array.isArray(cart.items) ? cart : await fetchCart();
    var items = Array.isArray(current.items) ? current.items : [];
    /** @type {Record<string, Array<Record<string, unknown>>>} */
    var groups = Object.create(null);

    items.forEach(function (raw) {
      var item = /** @type {Record<string, unknown>} */ (raw);
      var sku = normalizeSku(getLineSku(item));
      if (!sku) return;
      if (!groups[sku]) groups[sku] = [];
      groups[sku].push(item);
    });

    var changed = false;
    var skuKeys = Object.keys(groups);
    for (var g = 0; g < skuKeys.length; g += 1) {
      var group = groups[skuKeys[g]];
      if (!group || group.length < 2) continue;

      var canonical = chooseCanonicalLine(group);
      if (!canonical || !canonical.key) continue;
      var canonicalKeyBefore = String(canonical.key);

      /** @type {Array<{ weight: string, serial: string }>} */
      var units = [];
      group.forEach(function (line) {
        var props =
          line.properties && typeof line.properties === 'object'
            ? /** @type {Record<string, unknown>} */ (line.properties)
            : {};
        units = mergeSelectionUnits(units, expandSelectionUnits(readWeightCsv(props), readSerialCsv(props)));
      });

      var packed = unitsToProperties(units);
      var baseProps =
        canonical.properties && typeof canonical.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (canonical.properties)
          : {};
      var nextProps = buildCanonicalProperties(baseProps, {
        sku: getLineSku(canonical) || skuKeys[g],
        weightCsv: packed.weights,
        serialCsv: packed.serials,
      });
      var nextQty = packed.quantity > 0 ? packed.quantity : Math.max(1, Number(canonical.quantity) || 1);

      current = await changeLineByKey(canonicalKeyBefore, nextQty, nextProps);
      changed = true;

      /** @type {Record<string, number>} */
      var removals = {};
      group.forEach(function (line) {
        var key = String(line.key || '');
        if (!key || key === canonicalKeyBefore) return;
        removals[key] = 0;
      });
      if (Object.keys(removals).length) {
        var updated = await updateLinesByKeys(removals);
        if (updated) current = updated;
        else current = await fetchCart();
      } else {
        current = await fetchCart();
      }

      groups = Object.create(null);
      (Array.isArray(current.items) ? current.items : []).forEach(function (raw) {
        var item = /** @type {Record<string, unknown>} */ (raw);
        var sku = normalizeSku(getLineSku(item));
        if (!sku) return;
        if (!groups[sku]) groups[sku] = [];
        groups[sku].push(item);
      });
    }

    return { cart: current, changed: changed };
  }

  /**
   * Add items, merging into an existing same-SKU line when present.
   * @param {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} items
   * @param {{ consolidateFirst?: boolean }} [options]
   * @returns {Promise<{ cart: Record<string, unknown>, added: boolean }>}
   */
  function addOrMergeItems(items, options) {
    var list = Array.isArray(items) ? items.slice() : [];
    if (!list.length) return Promise.reject(new Error('No items to add'));

    return enqueue(async function () {
      var toastApi = w.AoneCartAddToast;
      // Own the success toast only when this call is the top-level user transaction.
      var ownsToast = toastApi && typeof toastApi.isSuppressed === 'function' && !toastApi.isSuppressed();
      if (ownsToast && typeof toastApi.beginSuppress === 'function') toastApi.beginSuppress();
      var succeeded = false;

      try {
        var opts = options || {};
        var cart = await fetchCart();

        if (opts.consolidateFirst !== false) {
          var consolidated = await consolidateDuplicatesUnlocked(cart);
          cart = consolidated.cart;
        }

        for (var i = 0; i < list.length; i += 1) {
          var item = list[i];
          var variantId = Number(item.id);
          if (!Number.isFinite(variantId) || variantId <= 0) continue;
          var qty = Math.max(0, Math.floor(Number(item.quantity) || 0));
          if (qty <= 0) continue;

          var incomingProps =
            item.properties && typeof item.properties === 'object'
              ? Object.assign({}, item.properties)
              : {};
          var sku = String(incomingProps.SKU || '').trim();
          var incomingUnits = expandSelectionUnits(
            readWeightCsv(incomingProps),
            readSerialCsv(incomingProps)
          );
          if (!incomingUnits.length) {
            for (var u = 0; u < qty; u += 1) incomingUnits.push({ weight: '', serial: '' });
          }

          var matches = sku ? findLinesForSku(cart, sku, variantId) : [];
          var canonical = chooseCanonicalLine(matches, variantId);

          if (canonical && canonical.key && sku) {
            var canonicalKeyBefore = String(canonical.key);
            /** @type {Array<{ weight: string, serial: string }>} */
            var existingUnits = [];
            matches.forEach(function (line) {
              var props =
                line.properties && typeof line.properties === 'object'
                  ? /** @type {Record<string, unknown>} */ (line.properties)
                  : {};
              existingUnits = mergeSelectionUnits(
                existingUnits,
                expandSelectionUnits(readWeightCsv(props), readSerialCsv(props))
              );
            });

            var mergedUnits = mergeSelectionUnits(existingUnits, incomingUnits);
            var packed = unitsToProperties(mergedUnits);
            var baseProps =
              canonical.properties && typeof canonical.properties === 'object'
                ? /** @type {Record<string, unknown>} */ (canonical.properties)
                : {};
            var nextProps = buildCanonicalProperties(baseProps, {
              sku: sku,
              weightCsv: packed.weights,
              serialCsv: packed.serials,
              incomingProps: incomingProps,
            });
            var nextQty =
              packed.quantity > 0 ? packed.quantity : qty + Math.max(0, Number(canonical.quantity) || 0);

            cart = await changeLineByKey(canonicalKeyBefore, nextQty, nextProps);

            /** @type {Record<string, number>} */
            var removals = {};
            matches.forEach(function (line) {
              var key = String(line.key || '');
              if (!key || key === canonicalKeyBefore) return;
              removals[key] = 0;
            });
            if (Object.keys(removals).length) {
              var updated = await updateLinesByKeys(removals);
              if (updated) cart = updated;
              else cart = await fetchCart();
            } else {
              cart = await fetchCart();
            }
          } else {
            var addProps = buildCanonicalProperties(
              {},
              {
                sku: sku,
                weightCsv: readWeightCsv(incomingProps),
                serialCsv: readSerialCsv(incomingProps),
                incomingProps: incomingProps,
              }
            );
            await addItemsRaw([
              {
                id: variantId,
                quantity: qty,
                properties: Object.keys(addProps).length ? addProps : undefined,
              },
            ]);
            cart = await fetchCart();
          }
        }

        succeeded = true;
        return { cart: cart, added: true };
      } finally {
        if (ownsToast) {
          if (typeof toastApi.endSuppress === 'function') toastApi.endSuppress();
          if (succeeded && typeof toastApi.showSuccess === 'function') toastApi.showSuccess();
        }
      }
    });
  }

  /**
   * Replace the cart line(s) for one SKU with an exact weight/serial selection.
   * Used by Edit Weight when shoppers reduce or change already-carted units.
   * @param {{
   *   id: number,
   *   sku: string,
   *   quantity: number,
   *   properties?: Record<string, string>,
   *   cart?: Record<string, unknown>,
   *   skipInitialFetch?: boolean
   * }} item
   * @returns {Promise<{ cart: Record<string, unknown>, updated: boolean }>}
   */
  function replaceSkuSelection(item) {
    return enqueue(async function () {
      var variantId = Number(item && item.id);
      var qty = Math.max(0, Math.floor(Number(item && item.quantity) || 0));
      var incomingProps =
        item && item.properties && typeof item.properties === 'object'
          ? Object.assign({}, item.properties)
          : {};
      var sku = normalizeSku(incomingProps.SKU || (item && item.sku) || '');
      if (!Number.isFinite(variantId) || variantId <= 0 || !sku) {
        throw new Error('Invalid cart replace payload');
      }

      var providedCart = item && item.cart;
      var cart =
        item &&
        item.skipInitialFetch &&
        providedCart &&
        Array.isArray(providedCart.items)
          ? /** @type {Record<string, unknown>} */ (providedCart)
          : await fetchCart();
      var matches = findLinesForSku(cart, sku, variantId);
      var canonical = chooseCanonicalLine(matches, variantId);

      if (qty <= 0) {
        /** @type {Record<string, number>} */
        var clearUpdates = {};
        matches.forEach(function (line) {
          var key = String(line.key || '');
          if (key) clearUpdates[key] = 0;
        });
        if (Object.keys(clearUpdates).length) {
          var cleared = await updateLinesByKeys(clearUpdates);
          cart = /** @type {Record<string, unknown>} */ (
            rememberCart(cleared) || (await fetchCart())
          );
        }
        return { cart: cart, updated: true };
      }

      var packedWeights = readWeightCsv(incomingProps);
      var packedSerials = readSerialCsv(incomingProps);
      var nextProps = buildCanonicalProperties(
        canonical && canonical.properties && typeof canonical.properties === 'object'
          ? /** @type {Record<string, unknown>} */ (canonical.properties)
          : {},
        {
          sku: sku,
          weightCsv: packedWeights,
          serialCsv: packedSerials,
          incomingProps: incomingProps,
        }
      );

      if (canonical && canonical.key) {
        var canonicalKey = String(canonical.key);
        // /cart/change.js returns the full cart — reuse it (no immediate /cart.js).
        cart = /** @type {Record<string, unknown>} */ (
          rememberCart(await changeLineByKey(canonicalKey, qty, nextProps))
        );
        /** @type {Record<string, number>} */
        var removals = {};
        matches.forEach(function (line) {
          var key = String(line.key || '');
          if (!key || key === canonicalKey) return;
          removals[key] = 0;
        });
        if (Object.keys(removals).length) {
          var updated = await updateLinesByKeys(removals);
          cart = /** @type {Record<string, unknown>} */ (
            rememberCart(updated) || (await fetchCart())
          );
        }
        return { cart: cart, updated: true };
      }

      await addItemsRaw([
        {
          id: variantId,
          quantity: qty,
          properties: Object.keys(nextProps).length ? nextProps : undefined,
        },
      ]);
      // /cart/add.js returns only added lines — full cart still requires /cart.js.
      cart = await fetchCart();
      return { cart: cart, updated: true };
    });
  }

  /**
   * Add multiple brand-new line items in a single /cart/add.js request.
   * Prefer this over calling replaceSkuSelection/addItemsRaw once per SKU.
   * @param {Array<{ id: number, quantity: number, properties?: Record<string, string> }>} items
   * @returns {Promise<{ cart: Record<string, unknown>, added: boolean }>}
   */
  function addItemsBatch(items) {
    var list = Array.isArray(items) ? items.slice() : [];
    if (!list.length) return Promise.reject(new Error('No items to add'));

    return enqueue(async function () {
      await addItemsRaw(list);
      // add.js does not return a full cart object.
      var cart = await fetchCart();
      return { cart: cart, added: true };
    });
  }

  /**
   * @param {Record<string, unknown>} [cart]
   * @returns {Promise<{ cart: Record<string, unknown>, changed: boolean }>}
   */
  function consolidateDuplicates(cart) {
    return enqueue(function () {
      return consolidateDuplicatesUnlocked(cart);
    });
  }

  w.AoneCartSkuMerge = {
    normalizeSku: normalizeSku,
    getLineSku: getLineSku,
    findLinesForSku: findLinesForSku,
    mergeSelectionUnits: mergeSelectionUnits,
    buildCanonicalProperties: buildCanonicalProperties,
    addOrMergeItems: addOrMergeItems,
    replaceSkuSelection: replaceSkuSelection,
    addItemsRaw: addItemsRaw,
    addItemsBatch: addItemsBatch,
    fetchCart: fetchCart,
    consolidateDuplicates: consolidateDuplicates,
    enqueue: enqueue,
  };
})(typeof window !== 'undefined' ? window : globalThis);
